/**
 * UN CLIENT, PLUSIEURS ADRESSES DE FACTURATION.
 *
 * Demandé par Amine le 18 septembre 2026 : « un client peut avoir des adresses
 * de facturation différentes — GT Solutions RS, Vitrolles et Paris. Je veux
 * pouvoir mettre les deux ; là j'ai dû ajouter un tiret devant l'un des
 * clients. »
 *
 * LE PRINCIPE : la fiche client garde SON adresse — la principale. Les autres
 * vivent à côté (« AdresseFacturation »), et une facture désigne celle qu'elle
 * vise. Un client qui n'en a qu'une n'a aucune ligne à côté, et rien ne change
 * pour lui.
 *
 * TOUT PASSE PAR UNE SUBSTITUTION. Plutôt que d'apprendre à chaque écran, au
 * PDF et au snapshot qu'une facture peut viser une autre adresse, on leur
 * donne un client dont l'adresse EST celle de la facture : « clientPourLeDocument »
 * remplace les colonnes d'adresse et rend une fiche que tout le reste lit
 * comme avant. Le snapshot fige donc les bonnes lignes sans rien savoir, et
 * le PDF d'un brouillon les imprime de même.
 *
 * Les colonnes latines de l'adresse sont VIDÉES à la substitution : « NULL »
 * y signifie « lire l'original », et l'original est désormais l'adresse
 * choisie. Une adresse supplémentaire non latine s'imprimerait donc telle
 * quelle — le cas n'existe pas aujourd'hui, et le PDF sait déjà le dessiner.
 */

export interface AdresseFacturation {
    id?: string;
    clientId?: string;
    /** Le nom sous lequel on la choisit — « Aulnay-sous-Bois », « Siège Paris ». */
    libelle: string;
    adresse?: string | null;
    adresse2?: string | null;
    codePostal?: string | null;
    ville?: string | null;
    pays?: string | null;
}

/** Ce qu'une fiche client doit porter pour que la substitution soit possible. */
export interface ClientAvecAdresses {
    adresse?: string | null;
    adresse2?: string | null;
    codePostal?: string | null;
    ville?: string | null;
    pays?: string | null;
    adresseLatin?: string | null;
    adresse2Latin?: string | null;
    villeLatin?: string | null;
    adressesFacturation?: AdresseFacturation[] | null;
}

/** L'adresse désignée, si le client la porte encore. `null` sinon. */
export function adresseDeFacturation(
    client: ClientAvecAdresses | null | undefined,
    adresseFacturationId: string | null | undefined
): AdresseFacturation | null {
    if (!client || !adresseFacturationId) return null;
    return (client.adressesFacturation ?? []).find((a) => a.id === adresseFacturationId) ?? null;
}

/**
 * La fiche client telle que le DOCUMENT la voit : son adresse est celle que
 * la facture vise. Sans adresse désignée — ou si elle a disparu de la fiche —
 * la fiche revient telle quelle : c'est l'adresse principale qui s'applique.
 */
export function clientPourLeDocument<T extends ClientAvecAdresses>(
    client: T,
    adresseFacturationId: string | null | undefined
): T {
    const choisie = adresseDeFacturation(client, adresseFacturationId);
    if (!choisie) return client;
    return {
        ...client,
        adresse: choisie.adresse ?? null,
        adresse2: choisie.adresse2 ?? null,
        codePostal: choisie.codePostal ?? null,
        ville: choisie.ville ?? null,
        pays: choisie.pays ?? client.pays ?? null,
        adresseLatin: null,
        adresse2Latin: null,
        villeLatin: null,
    };
}

/**
 * L'identifiant à ENREGISTRER sur un document : celui demandé s'il désigne
 * bien une adresse de CE client, `null` sinon. Un identifiant d'un autre
 * client, ou d'une adresse supprimée entre-temps, retombe sur la principale
 * plutôt que d'être écrit tel quel.
 */
export function adresseFacturationValide(
    client: ClientAvecAdresses | null | undefined,
    adresseFacturationId: string | null | undefined
): string | null {
    return adresseDeFacturation(client, adresseFacturationId)?.id ?? null;
}

/** Une ligne lisible — pour un menu, une infobulle. Accepte une fiche client aussi. */
export function resumeAdresse(
    a: Pick<AdresseFacturation, 'adresse' | 'adresse2' | 'codePostal' | 'ville'> | null | undefined
): string {
    if (!a) return '';
    const ville = [a.codePostal, a.ville].filter((x) => x && String(x).trim()).join(' ');
    return [a.adresse, a.adresse2, ville].filter((x) => x && String(x).trim()).join(', ');
}

/**
 * Le client a-t-il le choix ? Vrai dès qu'il porte une adresse en plus de la
 * principale : c'est à ce moment-là seulement que l'éditeur montre un menu.
 */
export function aPlusieursAdresses(client: ClientAvecAdresses | null | undefined): boolean {
    return (client?.adressesFacturation?.length ?? 0) > 0;
}
